<script setup lang="ts">
import { DatePicker } from 'v-calendar';
// import 'v-calendar/style.css';

// icons
import { faPlusCircle, faImages, faCalendarDay, faTimes } from '@fortawesome/pro-regular-svg-icons';

// validation
import { defineRule, useForm, useField } from 'vee-validate';
import { required } from '@vee-validate/rules';

// components
import CiModalSuccess from '@/components/CiModal/CiModalSuccess.vue';
import CiVideoUploadElement from '@/components/upload/CiVideoUploadElement.vue';

defineRule('required', required);
defineRule('isChecked', (val) => {
  if (Number(val) === 0) {
    return false;
  }

  return required(val);
});
defineRule('rangeOfStay', (val) => {
  if (!val || val.start === null || val.end === null) {
    return false;
  }
  return true;
});

const defaultFormData = {
  description: null,
  embed_param_h: null,
  title: null,
  uploaded_via_ci: false,
  video_url: null,
};

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiFormsUploadVideosProps {
  campsite: any;
}
const props = withDefaults(defineProps<CiFormsUploadVideosProps>(), {});

/********************
 * COMPOSITIONS      *
 ********************/
const localePath = useLocalePath();
const route = useRoute();
const videoService = useVideoService();
const { $gettext, interpolate } = useGettext();
const { getMsgWarning } = useSystemMessages();
const { i18AcceptTermsCheckboxText } = useI18nMeta();
const { locale } = useI18n();
const { openModal, closeModal } = useCiModal();
const viewport = useViewport();

/********************
 * REFS & VARS       *
 ********************/
const error = ref(null);
const errors = shallowRef(null);
const formRef = ref<HTMLFormElement | null>(null);
const forms = reactive([{ ...defaultFormData }]);
const monthOfStay = ref(null);
const loading = ref(false);
const success = ref(null);
const uploadPending = ref(false);

// form
const { validate } = useForm({});
const { value: termsValue, errorMessage: termsError } = useField<boolean>('terms', 'isChecked');
const { value: rangeOfStayValue, errorMessage: rangeOfStayError } = useField<{ start: Date; end: Date }>(
  'rangeOfStay',
  'rangeOfStay',
);
/********************
 * FUNCTIONS         *
 ********************/
function onElementChange(payload, idx) {
  forms[idx] = payload;
}

function onDateSelect(value) {
  if (!value || !value.start) {
    rangeOfStayValue.value = { start: null, end: null };
    monthOfStay.value = null;
    return;
  }

  monthOfStay.value = value.start?.toISOString().split('T')[0];
}

function onTermsCheckboxClicked(payload) {
  termsValue.value = payload;
}

function addForm() {
  forms.push({ ...defaultFormData });
}

function removeForm(idx) {
  forms.splice(idx, 1);
}

function getErrors(idx) {
  if (errors.value?.[idx]) {
    return errors.value[idx];
  }
  return {};
}

function resetForm() {
  loading.value = false;
  success.value = false;
  formRef.value?.reset();
}

function successText() {
  const campsiteName = props.campsite?.meta ? props.campsite.meta.name : 'unknown campsite';
  const vidCount = forms.length;

  if (vidCount > 1) {
    const txt = $gettext('Your %{count} Videos for %{campsite} where successfully created.');
    return interpolate(txt, { count: vidCount, campsite: campsiteName.trim() }, true);
  }
  const txt = $gettext('Your Video for %{campsite} was successfully created.');
  return interpolate(txt, { campsite: campsiteName.trim() }, true);
}

function cancel() {
  return navigateTo(localePath({ name: RBN_CAMPSITE_DETAIL, params: { identifier: route.params.identifier } }));
}

function openModalSuccess(metaData) {
  const warningKeys = Object.keys(metaData.warnings ? metaData.warnings : {});
  const warnings = [];
  warningKeys.forEach((key) => {
    warnings.push(getMsgWarning(key));
  });

  openModal(
    {
      component: CiModalSuccess,
      attrs: {
        name: 'CiModalSuccess',
        buttons: [
          {
            label: $gettext('Back to campsite'),
            to: localePath({
              name: RBN_CAMPSITE_DETAIL,
              params: { identifier: props.campsite.slug },
            }),
            classes: ['button--primary'],
          },
        ],
        closeable: false,
        successMessage: successText(),
        title: $gettext('Saving process successful'),
        warnings,
        onClose: () => {
          closeModal('CiModalSuccess');
        },
      },
    },
    {
      clickToClose: false,
      contentTransition: 'translate-y-down',
      contentClass: 'max-w-[800px] w-full h-auto',
    },
  );
}

async function onSubmitClicked() {
  const { valid } = await validate();
  if (!valid) {
    scrollToError();
    return false;
  }

  error.value = null;
  errors.value = null;
  loading.value = true;
  success.value = null;

  forms.map((form) => {
    // Cleanup null values from form object
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    Object.keys(form).forEach(key => form[key] == null && delete form[key]);
    // Set global form params to each video form data
    Object.assign(form, {
      campsite: props.campsite.slug,
      lang: locale.value,
      month_of_stay: monthOfStay.value,
    });
    return form;
  });

  if (forms.length) {
    forms[0].marketing_signup = 'true';
  }

  videoService
    .create(locale.value, forms)
    .then((res) => {
      if (res) {
        loading.value = false;

        if (res) {
          openModalSuccess(res.meta);
          success.value = successText();
          forms.splice(0, forms.length);
          nextTick(() => {
            forms[0] = { ...defaultFormData };
            window.scrollTo(0, 0);
            resetForm();
          });
        }
      }
    })
    .catch((err) => {
      if (err?.response?._data) {
        loading.value = false;
        errors.value = err?.response?._data;
      }
    });
  return false;
}

function scrollToError() {
  nextTick(() => {
    scrollToElement(formRef.value, 'has-error');
  });
}

function onElementLoading(payload: boolean) {
  uploadPending.value = payload;
}
</script>

<template>
  <transition
    name="slide-fade"
    appear
  >
    <form
      ref="formRef"
      novalidate
      @submit.prevent="onSubmitClicked"
    >
      <div class="mb-20">
        <div class="container">
          <div class="row">
            <div class="col-12">
              <h3>{{ $gettext('Add Videos') }}</h3>
              <p>{{ $gettext('Upload-Videos-Subline') }}</p>
            </div>
          </div>

          <div class="row">
            <div class="col-12">
              <CiVideoUploadElement
                v-for="(element, idx) in forms"
                :key="idx"
                ref="videoForms"
                :index="idx"
                :form="element"
                :errors="getErrors(idx)"
                :failed-rules="{}"
                :show-remove-button="idx > 0"
                :disabled="loading || uploadPending"
                :name="`videoForm_${idx}`"
                :campsite="props.campsite"
                @input="onElementChange($event, idx)"
                @remove="removeForm(idx)"
                @loading="onElementLoading"
              />
            </div>
          </div>

          <!-- add more -->
          <div class="row">
            <div class="col-12">
              <div class="my-6 rounded-md border border-dotted border-black p-4">
                <div class="row">
                  <div class="col-12 col-lg-8">
                    <div class="flex items-center">
                      <CiAwesomeIcon
                        :icon="faImages"
                        ratio="1.5"
                        class="fill-black"
                      />
                      <span class="ml-3">{{ $gettext('Upload more videos and add a description') }}</span>
                    </div>
                  </div>
                  <div class="col-12 col-lg-4 mt-2 flex justify-center lg:mt-0 lg:justify-end">
                    <CiButton
                      :disabled="loading"
                      class="button button--info button--radius-small"
                      :icon="faPlusCircle"
                      icon-ratio="0.75"
                      icon-before
                      @click="addForm"
                    >
                      <span>{{ $gettext('Add more') }}</span>
                    </CiButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- date -->
          <div class="row mt-12">
            <div class="col-12 col-lg-5 col-md-6">
              <div
                class="mb-6"
                :class="{ 'has-error': rangeOfStayError }"
              >
                <label class="input-label font-medium"><span>{{ $gettext('Date of stay') }}</span><sup>*</sup></label>
                <div class="input-field input--inverted !p-0">
                  <DatePicker
                    v-model.range="rangeOfStayValue"
                    :locale="locale"
                    color="green"
                    :initial-page="{ month: new Date().getMonth() + 1, year: new Date().getFullYear() }"
                    :max-date="new Date()"
                    :masks="{ input: 'D. MMMM YYYY' }"
                    :update-on-input="false"
                    :columns="viewport.isLessThan('md') ? 1 : 2"
                    name="rangeOfStay"
                    @update:model-value="onDateSelect"
                  >
                    <template #default="{ inputValue, togglePopover }">
                      <div
                        class="flex size-full flex-row items-center rounded p-4"
                        :class="[
                          loading ? 'pointer-events-none cursor-not-allowed bg-gray-30' : 'cursor-pointer bg-gray-10',
                        ]"
                        @click="togglePopover"
                      >
                        <CiAwesomeIcon
                          :icon="faCalendarDay"
                          class=""
                        />
                        <span
                          v-if="inputValue.start && inputValue.end"
                          class="ml-2"
                        >{{ inputValue.start }} - {{ inputValue.end }}</span>
                        <span
                          v-else
                          class="ml-2 text-gray-light"
                        > {{ $gettext('Select date of stay...') }}</span>
                        <CiAwesomeIcon
                          v-if="rangeOfStayValue?.start || rangeOfStayValue?.end"
                          :icon="faTimes"
                          class="ml-auto"
                          @click.stop.prevent="() => onDateSelect(null)"
                        />
                      </div>
                    </template>
                  </DatePicker>
                </div>
              </div>
            </div>
          </div>

          <!-- terms -->
          <div class="row">
            <div class="col-12">
              <InputCheckbox
                id="terms"
                :allow-html="true"
                :has-error="!!termsError"
                :label="i18AcceptTermsCheckboxText"
                :value="termsValue"
                name="terms"
                @input="onTermsCheckboxClicked"
              />
            </div>
          </div>

          <div class="row mt-4">
            <div class="col-12">
              <span class="text-small"><sup>*</sup><span>{{ $gettext('Mandatory fields') }}</span></span>
            </div>
          </div>

          <div class="row mt-4">
            <div class="col-12">
              {{ errors }}
              <CiInfoPanel
                :condition="errors !== null && errors.length > 0"
                class="mt-6"
                type="danger"
              >
                <CiDjangoErrorMessages :errors="errors" />
              </CiInfoPanel>
            </div>
          </div>

          <div class="row my-4">
            <div class="col-12 col-md-6 col-lg-3 offset-lg-3 order-2 mt-4 md:order-1 md:mt-0">
              <button
                class="button button--dark-outline w-full"
                type="button"
                @click="cancel"
              >
                {{ $gettext('Cancel') }}
              </button>
            </div>
            <div class="col-12 col-md-6 col-lg-3 order-1 md:order-2">
              <button
                :disabled="loading"
                class="button w-full"
                :class="[loading ? 'button--disabled' : 'button--primary']"
                type="button"
                @click="onSubmitClicked"
              >
                <span>{{ $gettext('Send') }}</span>
                <CiSpinner
                  v-if="loading"
                  class="icon icon--loading"
                  type="white"
                  size="sm"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  </transition>
</template>

<style></style>
